import { useState } from 'react';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import toast from 'react-hot-toast';
import { PATHS } from '@client/routes';
import { CopyToClipboard } from '@client/new/components/copy-to-clipboard';
import { ResetDomainModal } from '@client/new/pages/creator/settings/domain/DomainSetup/ResetDomainModal';
import {
  CustomDomainStatus,
  useCustomDomainsQuery,
  useRemoveCustomDomainMutation,
} from 'generated/graphql';
import { AccordionHeader } from './AccordionHeader';
import { sectionConfigs } from './sectionConfigs';

const DOMAIN_BADGES = {
  notSetUp: { label: 'Not set up', color: '#cc7c1e' },
  inProgress: { label: 'In progress', color: '#448af7' },
  setUp: { label: 'Set up', color: '#0acc2f' },
} as const;

const getDomainBadge = (domain: { status: CustomDomainStatus } | null) => {
  if (!domain) {
    return DOMAIN_BADGES.notSetUp;
  }
  return domain.status === CustomDomainStatus.InfraRunning
    ? DOMAIN_BADGES.setUp
    : DOMAIN_BADGES.inProgress;
};

const StatusBadge = ({ label, color }: { label: string; color: string }) => (
  <span
    className="text-xs font-medium uppercase tracking-[1px]"
    style={{ color }}
  >
    {label}
  </span>
);

const SetUpButton = ({ influencerId }: { influencerId: number }) => (
  <Link
    to={PATHS.CUSTOM_DOMAIN_SETUP(influencerId)}
    className="bg-[#ede5fc] text-[#350584] text-sm font-medium px-4 py-2 rounded-[10px] no-underline whitespace-nowrap"
  >
    Set up
  </Link>
);

export const CustomDomainCard = ({
  influencerId,
}: {
  influencerId: number;
}) => {
  const { data, loading, error, refetch } = useCustomDomainsQuery({
    variables: { id: influencerId },
    fetchPolicy: 'no-cache',
  });
  const [showResetModal, setShowResetModal] = useState(false);
  const [removeDomain, { loading: resetting }] =
    useRemoveCustomDomainMutation();

  const domain =
    !loading && !error && data?.creator?.customDomains?.length
      ? data.creator.customDomains[0]
      : null;
  const badge = getDomainBadge(domain);
  const domainUrl = domain ? `https://${domain.domain}` : '';

  const handleReset = () => {
    if (!domain) {
      return;
    }
    removeDomain({ variables: { domainId: domain.id, influencerId } })
      .then(() => {
        toast.success('Domain has been reset successfully!');
        refetch();
      })
      .catch(() => {
        toast.error('Something went wrong with domain reset!');
      })
      .finally(() => {
        setShowResetModal(false);
      });
  };

  return (
    <div className="bg-white rounded-xl p-4">
      <div className="flex items-center justify-between gap-4">
        <AccordionHeader
          icon={<FontAwesomeIcon icon={faGlobe} />}
          {...sectionConfigs.customDomain}
          badge={<StatusBadge label={badge.label} color={badge.color} />}
        />
        {!domain && <SetUpButton influencerId={influencerId} />}
      </div>

      {domain && (
        <div className="flex items-end gap-3 mt-4">
          <label className="flex flex-col gap-1 flex-1 min-w-0">
            <span className="text-sm font-medium text-black px-1">
              Your domain
            </span>
            <div className="flex items-center gap-4 border-2 border-[#e4e4e4] rounded-[10px] px-4 py-1">
              <span className="flex-1 min-w-0 truncate text-base font-medium text-[#646464]">
                {domainUrl}
              </span>
              <CopyToClipboard contentToCopy={domainUrl} />
            </div>
          </label>
          <button
            type="button"
            onClick={() => setShowResetModal(true)}
            disabled={resetting}
            className="bg-[#ede5fc] text-[#350584] text-sm font-medium px-4 py-2 rounded-[10px] whitespace-nowrap"
          >
            Reset
          </button>
        </div>
      )}

      <ResetDomainModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
        isSubmitting={resetting}
      />
    </div>
  );
};
